import { useCallback, useMemo, useState } from 'react';
import { COVER_REDO_KINDS } from 'fym-shared/src/music24/coverRedo';
import { dateRedoItemsToMake, type DateRedoState } from 'fym-shared/src/music24/dateRedo';
import { usePreviewDateRedoMutation, useStartDateRedoMutation } from '../../../../../../redux/api/fymAPI';

/**
 * The confirm dialog of the date redo. Open reads a fresh dry run (it makes nothing); confirm sends
 * the post switch. The redo is free, so there is no cost to confirm. Every group counts as ticked
 * for the post part of the cover redo (`CoverRedoPosts`).
 */
export function useDateRedoDialog(input: { accountId: number; catalogItemUuid: string }) {
  const { accountId, catalogItemUuid } = input;
  const [previewRedo] = usePreviewDateRedoMutation();
  const [startRedo, startState] = useStartDateRedoMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<DateRedoState>();
  const [swapPosts, setSwapPosts] = useState(true);
  const [failed, setFailed] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
    setState(undefined);
    setFailed(false);
    setSwapPosts(true);
    previewRedo({ accountId, catalogItemUuid, swapPosts: true, dryRun: true })
      .unwrap()
      .then(setState)
      .catch((reason: unknown) => {
        console.error('Failed to read the date redo', reason);
        setFailed(true);
      });
  }, [accountId, catalogItemUuid, previewRedo]);

  const close = useCallback(() => {
    if (!startState.isLoading) {
      setIsOpen(false);
    }
  }, [startState.isLoading]);

  const toggleSwap = useCallback(() => setSwapPosts((current) => !current), []);
  const count = useMemo(() => dateRedoItemsToMake(state?.items ?? []).length, [state]);

  const confirm = useCallback(() => {
    if (state === undefined || count === 0) {
      return;
    }
    setFailed(false);
    startRedo({ accountId, catalogItemUuid, swapPosts })
      .unwrap()
      .then(() => setIsOpen(false))
      .catch((reason: unknown) => {
        console.error('Failed to redo the kit with the new release date', reason);
        setFailed(true);
      });
  }, [accountId, catalogItemUuid, count, startRedo, state, swapPosts]);

  return {
    isOpen,
    state,
    ticked: COVER_REDO_KINDS,
    swapPosts,
    count,
    failed,
    isStarting: startState.isLoading,
    open,
    close,
    toggleSwap,
    confirm,
  };
}

export type DateRedoDialogModel = ReturnType<typeof useDateRedoDialog>;
